import React, { useState, useEffect, useRef } from "react";

/**
 * TVMediaCarousel
 *
 * Componente de exibição de UMA mídia por vez em fullscreen.
 * - type "single": exibe a imagem única
 * - type "carousel": roda as imagens em sequência com fade
 *
 * Duração por slide: usa o `slideDuration` da mídia (padrão 8s).
 * O componente pai (useTVDisplay) é quem controla QUAL mídia exibir
 * e quando alternar entre mídia e tela de aulas.
 */
const TVMediaCarousel = ({ media }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const timerRef = useRef(null);

    // Resolver as imagens desta mídia
    const slides = React.useMemo(() => {
        if (!media) return [];
        if (media.type === "carousel" && media.carouselImages?.length > 0) {
            return media.carouselImages
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(img => img.url);
        }
        return media.imageUrl ? [media.imageUrl] : [];
    }, [media]);

    const slideDurationMs = ((media?.slideDuration || 8) * 1000);

    // Reset slide quando a mídia muda
    useEffect(() => {
        setCurrentSlide(0);
        setIsTransitioning(false);
    }, [media?.id]);

    // Timer para avançar slides (só para carousel com mais de 1 slide)
    useEffect(() => {
        if (slides.length <= 1) return;

        timerRef.current = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentSlide(prev => (prev + 1) % slides.length);
                setIsTransitioning(false);
            }, 600); // duração do fade-out
        }, slideDurationMs);

        return () => clearInterval(timerRef.current);
    }, [slides.length, slideDurationMs]);

    if (!media || slides.length === 0) return null;

    const currentImageUrl = slides[currentSlide] || slides[0];

    return (
        <div
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 1,
                overflow: "hidden",
                backgroundColor: "#000",
            }}
        >
            <img
                key={`${media.id}-${currentSlide}`}
                src={currentImageUrl}
                alt={media.name || "Mídia"}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: isTransitioning ? 0 : 1,
                    transition: "opacity 0.6s ease-in-out",
                }}
            />

            {/* Indicadores (dots) — só para carousel com múltiplos slides */}
            {slides.length > 1 && (
                <div
                    style={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        display: "flex",
                        gap: 6,
                        zIndex: 5,
                    }}
                >
                    {slides.map((_, idx) => (
                        <div
                            key={idx}
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: idx === currentSlide ? "#fff" : "rgba(255,255,255,0.4)",
                                transition: "background-color 0.3s",
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default TVMediaCarousel;
